import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Badge } from '../catalyst/badge'
import { Button } from '../catalyst/button'
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../catalyst/description-list'
import { Heading, Subheading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { formatDate } from '../lib/farm-log-layout'
import { useFarms } from '../lib/farms'
import { CATEGORY_LABEL, inventoryPath, useAsset, useAssetRecords } from '../lib/inventory'
import type { Asset, AssetRecords } from '../lib/types'
import { Shell } from './shell'

// One piece of equipment or one building: what it is, and every record kept against it.

/** One linked record, for the asset's history: what it was, on which log page. */
type RecordLine = { id: string; date: string; title: string; detail: string; href: string }

function recordLines(records: AssetRecords, farmName: (farmId: string) => string): RecordLine[] {
  return [
    ...records.equipmentActivities.map((r) => ({
      id: r.id,
      date: r.performedOn,
      title: `Form I · ${r.activityType}`,
      detail: [r.description, farmName(r.farmId)].filter(Boolean).join(' · '),
      href: `/farms/${r.farmId}/equipment-activities`,
    })),
    ...(records.sprayLogs ?? []).map((r) => ({
      id: r.id,
      date: r.appliedOn,
      title: 'Form H1 · spray',
      detail: [r.applicatorName && `by ${r.applicatorName}`, farmName(r.farmId)].filter(Boolean).join(' · '),
      href: `/farms/${r.farmId}/fields/${r.fieldId}/spray-logs`,
    })),
    ...(records.fertilizerLogs ?? []).map((r) => ({
      id: r.id,
      date: r.appliedOn,
      title: `Form H2 · ${r.blend}`,
      detail: [r.applicationMethod === 'drip' ? 'fertigation' : null, farmName(r.farmId)].filter(Boolean).join(' · '),
      href: `/farms/${r.farmId}/fields/${r.fieldId}/fertilizer-logs`,
    })),
    ...records.buildingInspections.map((r) => ({
      id: r.id,
      date: r.inspectedOn,
      title: 'Form G · inspection',
      detail: [r.completedBy ? `by ${r.completedBy}` : null, farmName(r.farmId)].filter(Boolean).join(' · '),
      href: `/farms/${r.farmId}/building-inspections`,
    })),
    ...records.pestMonitoringChecks.map((r) => ({
      id: r.id,
      date: r.checkedOn,
      title: `Form M · ${r.deviceOrArea}`,
      detail: [r.findings, farmName(r.farmId)].filter(Boolean).join(' · '),
      href: `/farms/${r.farmId}/pest-monitoring-checks`,
    })),
  ].sort((a, b) => b.date.localeCompare(a.date))
}

function Records({ asset, farmId }: { asset: Asset; farmId: string }) {
  const { org } = useAuth()
  const { getFarm } = useFarms()
  const { records, error } = useAssetRecords(org?.id, asset.id)
  const farmName = (id: string) => (id === farmId ? '' : (getFarm(id)?.name ?? 'another farm'))

  if (error) return <Text className="mt-2 text-red-600 dark:text-red-400">{error}</Text>
  if (!records) return <Text className="mt-2">Loading…</Text>
  const lines = recordLines(records, farmName)
  if (lines.length === 0) {
    return (
      <Text className="mt-2">
        Nothing logged against it yet. Pick it by name when you log {asset.category === 'BUILDING' ? 'an inspection or a pest check' : 'cleaning, maintenance, calibration or an application'}.
      </Text>
    )
  }
  return (
    <ul className="mt-2 divide-y divide-zinc-950/5 dark:divide-white/5">
      {lines.map((line) => (
        <li key={line.id}>
          <Link href={line.href} className="flex items-baseline justify-between gap-4 py-3">
            <span className="min-w-0">
              <span className="block text-base/6 font-medium text-zinc-950 sm:text-sm/6 dark:text-white">{line.title}</span>
              {line.detail && <span className="block truncate text-sm/6 text-zinc-500 dark:text-zinc-400">{line.detail}</span>}
            </span>
            <span className="shrink-0 text-sm/6 text-zinc-500 dark:text-zinc-400">{formatDate(line.date)}</span>
          </Link>
        </li>
      ))}
    </ul>
  )
}

export function AssetPage() {
  const { farmId, assetId } = useParams<{ farmId: string; assetId: string }>()
  const navigate = useNavigate()
  const { org } = useAuth()
  const { getFarm, loading: farmsLoading } = useFarms()
  const { asset, loading, error, archiveAsset } = useAsset(org?.id, assetId)
  const [confirming, setConfirming] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const canWrite = org?.role !== 'AGRONOMIST'

  const farm = farmId ? getFarm(farmId) : undefined
  if (!farmsLoading && !farm) return <Navigate to="/" replace />
  if (!farm || !org || !farmId) return null

  const back = inventoryPath(farmId, asset?.category)

  async function handleArchive() {
    if (!asset || !farmId) return
    setArchiving(true)
    setArchiveError(null)
    try {
      await archiveAsset()
      navigate(inventoryPath(farmId, asset.category), { replace: true })
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Could not archive it.')
      setArchiving(false)
    }
  }

  return (
    <Shell>
      <Link href={back} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {asset ? CATEGORY_LABEL[asset.category].plural : 'Equipment and buildings'}
      </Link>

      {loading ? (
        <Text className="mt-6">Loading…</Text>
      ) : !asset ? (
        <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>{error ?? 'Not found.'}</Text>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <Heading className="flex flex-wrap items-center gap-2">
                {asset.name}
                {asset.kind === 'MOVABLE' && <Badge color="zinc">All farms</Badge>}
                {asset.archivedAt && <Badge color="amber">Archived</Badge>}
              </Heading>
              <Text>{[asset.type, asset.makeModel].filter(Boolean).join(' · ')}</Text>
            </div>
            {canWrite && !asset.archivedAt && (
              <div className="flex gap-3">
                <Button outline onClick={() => setConfirming(true)} disabled={confirming}>
                  Archive
                </Button>
                <Button href={`/farms/${farmId}/inventory/${asset.id}/edit`}>Edit</Button>
              </div>
            )}
          </div>

          {confirming && (
            <div className="mt-6 rounded-lg bg-zinc-50 p-4 ring-1 ring-zinc-950/10 dark:bg-white/5 dark:ring-white/10">
              <Text>Archive {asset.name}? It is hidden from lists and pickers. Its records stay.</Text>
              {archiveError && <Text className="mt-2 text-red-600 dark:text-red-400">{archiveError}</Text>}
              <div className="mt-4 flex gap-3">
                <Button color="red" onClick={handleArchive} disabled={archiving}>
                  {archiving ? 'Archiving…' : 'Archive'}
                </Button>
                <Button plain onClick={() => setConfirming(false)} disabled={archiving}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <DescriptionList className="mt-8">
            <DescriptionTerm>Type</DescriptionTerm>
            <DescriptionDetails>{asset.type}</DescriptionDetails>
            {asset.category === 'EQUIPMENT' && (
              <>
                <DescriptionTerm>Make and model</DescriptionTerm>
                <DescriptionDetails>{asset.makeModel ?? '—'}</DescriptionDetails>
              </>
            )}
            <DescriptionTerm>Where</DescriptionTerm>
            <DescriptionDetails>{asset.farmId ? (getFarm(asset.farmId)?.name ?? farm.name) : 'Moves between all your farms'}</DescriptionDetails>
            <DescriptionTerm>Description</DescriptionTerm>
            <DescriptionDetails className="whitespace-pre-line">{asset.description ?? '—'}</DescriptionDetails>
          </DescriptionList>

          <Subheading className="mt-10">Records</Subheading>
          <Records asset={asset} farmId={farmId} />
        </>
      )}
    </Shell>
  )
}
